/**
 * @fileoverview Google Analytics Enhanced Ecommerce tracking triggers
 *
 * Tracks add-to-cart, remove-from-cart, quantity changes, product link clicks,
 * and product-card image carousel navigation. All cart events are sourced from
 * the cart-state pub-sub system (utilities-events.js) rather than DOM scraping,
 * so the data is always accurate and no extra network fetches are required.
 *
 * Product link clicks use sessionStorage to bridge the click event (on a list
 * page) to the detail page load where GA processes the productClick event.
 */

import { subscribe, EVENTS } from '@utilities/events';
import { getCartStateManager } from '@utilities/cart-state-manager';

/** @type {string} */
const storeCurrencyCode = window.theme?.currency?.code;

/** @type {string} */
const sessionStorageProductClick = 'datalayer:product:click';

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Safely push to the GA data layer, initialising it if absent.
 * @param {Object} payload
 */
function pushToDataLayer(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Convert Shopify price in cents to a decimal number.
 * @param {number} cents
 * @returns {number}
 */
function centsToDecimal(cents) {
  return parseFloat((cents / 100).toFixed(2));
}

// ─── Add to cart ──────────────────────────────────────────────────────────

/**
 * Build a GA eec.addToCart payload from a cart state line item.
 * @param {Object} item - Enriched cart line item from cart-state
 * @param {number} quantity - Quantity added (may differ from item.quantity)
 * @returns {Object}
 */
function buildAddToCartPayload(item, quantity) {
  return {
    event: 'eec.addToCart',
    ecommerce: {
      currencyCode: storeCurrencyCode,
      add: {
        products: [{
          name: item.productTitle,
          id: item.productId,
          price: centsToDecimal(item.variantPrice),
          brand: item.productVendor,
          category: item.productType,
          variant: item.variantTitle || item.title,
          quantity,
        }],
      },
    },
  };
}

// Listen for successful cart adds.
// `items` is the array of { id: variantId, quantity } objects sent to Shopify.
// `state.items` is the enriched cart (camelCase, always consistent).
// We resolve each requested variant against state.items to guarantee we read
// the enriched shape regardless of which path addedLineItems came from.
subscribe(EVENTS.CART_STATE_ITEM_ADDED, ({ items: requestedItems, state }) => {
  if (!Array.isArray(requestedItems) || !requestedItems.length) return;
  if (!Array.isArray(state?.items)) return;

  for (const req of requestedItems) {
    const variantId = Number(req.id);
    const enriched = state.items.find(i => Number(i.variantId) === variantId);
    if (!enriched) continue;
    pushToDataLayer(buildAddToCartPayload(enriched, req.quantity || 1));
  }
});

// ─── Remove from cart ────────────────────────────────────────────────────

/**
 * Build a GA eec.removeFromCart payload from a cart state line item.
 * @param {Object} item - Enriched cart line item from cart-state
 * @param {number} quantity - Quantity removed
 * @returns {Object}
 */
function buildRemoveFromCartPayload(item, quantity) {
  return {
    event: 'eec.removeFromCart',
    ecommerce: {
      currencyCode: storeCurrencyCode,
      remove: {
        products: [{
          name: item.productTitle,
          id: item.productId,
          price: centsToDecimal(item.variantPrice),
          brand: item.productVendor,
          category: item.productType,
          variant: item.variantTitle || item.title,
          quantity,
        }],
      },
    },
  };
}

// `items` in CART_STATE_ITEM_REMOVING is snapshotted before the remove API
// call, so it carries the quantity that was in the cart at removal time.
subscribe(EVENTS.CART_STATE_ITEM_REMOVING, ({ items }) => {
  if (!Array.isArray(items) || !items.length) return;

  for (const item of items) {
    pushToDataLayer(buildRemoveFromCartPayload(item, item.quantity));
  }
});

// ─── Quantity changes (increase / decrease from cart) ────────────────────

// Snapshot the old quantity before the API call so we can compute the delta.
// Key → previous quantity, cleared after CART_STATE_ITEM_CHANGED fires.
const previousQuantityByKey = new Map();

subscribe(EVENTS.CART_STATE_ITEM_CHANGING, ({ key, changes }) => {
  if (changes?.quantity == null) return;
  const item = getCartStateManager()?.getItem(key);
  if (item) previousQuantityByKey.set(key, item.quantity);
});

// `changes.quantity` is the new absolute quantity (e.g. 4), not a delta.
// `state.items` is the enriched post-update cart.
subscribe(EVENTS.CART_STATE_ITEM_CHANGED, ({ key, changes, state }) => {
  if (changes?.quantity == null) return;

  const oldQuantity = previousQuantityByKey.get(key);
  previousQuantityByKey.delete(key);
  if (oldQuantity == null) return;

  const newQuantity = changes.quantity;
  const delta = newQuantity - oldQuantity;
  if (delta === 0) return;

  const item = (state?.items || []).find(i => i.key === key);
  if (!item) return;

  if (delta > 0) {
    pushToDataLayer(buildAddToCartPayload(item, delta));
  } else {
    pushToDataLayer(buildRemoveFromCartPayload(item, Math.abs(delta)));
  }
});

// ─── Product link clicks ─────────────────────────────────────────────────

// On page load: if the previous page stored a product click, fire the GA event.
const storedClick = sessionStorage.getItem(sessionStorageProductClick);
if (storedClick) {
  const data = JSON.parse(storedClick);
  sessionStorage.removeItem(sessionStorageProductClick);

  const productUrl = data.productHref.split('?')[0];
  fetch(`${productUrl}.json`)
    .then(response => response.json())
    .then(({ product }) => {
      if (!product) return;
      pushToDataLayer({
        event: 'eec.productClick',
        ecommerce: {
          click: {
            actionField: { list: data.listType || '' },
            products: [{
              name: product.title,
              id: product.id,
              price: centsToDecimal(product.price),
              brand: product.vendor,
              category: product.type,
              variant: '',
              position: data.position,
            }],
          },
        },
      });
    });
}

/**
 * Store product click data in sessionStorage for pickup on the product page.
 * Also records list type and click position for the GA actionField.
 * @param {MouseEvent} event
 */
function dataLayerHandleProductLinkClick(event) {
  const link = event.target.closest('a[href^="/products/"]');
  if (!link) return;

  // Determine list type from Shopify page type.
  const pageType = window.theme?.request?.page_type;
  let listType = '';
  if (pageType === 'search') {
    listType = 'Search results';
  } else if (pageType === 'collection') {
    listType = 'Collection page';
  }

  // Derive click position from the link's product-card index in the grid.
  const productCard = link.closest('product-card');
  const position = productCard
    ? Array.from(document.querySelectorAll('product-card')).indexOf(productCard) + 1
    : 0;

  sessionStorage.setItem(sessionStorageProductClick, JSON.stringify({
    productHref: link.href,
    listType,
    position,
  }));
}

document.addEventListener('click', dataLayerHandleProductLinkClick);

// ─── Product card image carousel navigation ──────────────────────────────

/**
 * Push a view_plp_card_image event when the user swipes to a new image.
 * @param {HTMLElement} productCard - The product-card custom element
 */
function pushProductCardImageNavEvent(productCard) {
  if (!productCard) return;
  pushToDataLayer({
    event: 'view_plp_card_image',
    ecommerce: {
      page_path: window.location.pathname,
      items: [{
        item_id: productCard.variant?.id,
        item_name: productCard.product?.title,
        item_image_url: productCard.variant?.featured_image,
      }],
    },
  });
}

/**
 * Bind an Embla `select` handler to one product-card carousel.
 * Retries once after 100 ms if the Embla API isn't available yet.
 * @param {HTMLElement} sliderElement
 * @param {HTMLElement} productCard
 */
function bindEmblaSelectEvent(sliderElement, productCard) {
  if (!sliderElement || !productCard) return;
  if (sliderElement.hasAttribute('data-embla-select-bound')) return;

  const bind = () => {
    const emblaApi = productCard.slider || productCard.sliderComponent?.emblaApi || sliderElement.emblaApi;
    if (!emblaApi || typeof emblaApi.on !== 'function') return false;

    sliderElement.setAttribute('data-embla-select-bound', 'true');
    let previousIndex = emblaApi.selectedScrollSnap?.() ?? null;

    emblaApi.on('select', () => {
      const selectedIndex = emblaApi.selectedScrollSnap?.() ?? null;
      if (selectedIndex === previousIndex) return;
      previousIndex = selectedIndex;
      pushProductCardImageNavEvent(productCard);
    });

    return true;
  };

  if (!bind()) {
    setTimeout(bind, 100);
  }
}

/**
 * Bind Embla select tracking to all existing and future product-card carousels.
 */
function observeProductCardEmblaSliders() {
  for (const slider of document.querySelectorAll('product-card .plp-card-carousel.embla-initialized')) {
    const productCard = slider.closest('product-card');
    if (productCard) bindEmblaSelectEvent(slider, productCard);
  }

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const candidates = node.classList?.contains('embla-initialized')
          ? [node]
          : node.querySelectorAll?.('.plp-card-carousel.embla-initialized') || [];

        for (const el of candidates) {
          const productCard = el.closest('product-card');
          if (productCard) setTimeout(() => bindEmblaSelectEvent(el, productCard), 100);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

observeProductCardEmblaSliders();
